// CreateVariantModal.tsx
import React, { useEffect, useState } from "react"
import { Modal, Typography } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { notify } from "shared/ui/Notifications/notify"
import CreateVariantForm from "./CreateVariantForm.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons"
import { Project } from "stores/projectStore"
import { Variant, SelectedVariant } from "types/questionnaire.types"

/**
 * Props interface for the CreateVariantModal component
 */
interface CreateVariantModalProps {
  /** The project data */
  project: Project
  /** Function to insert variant text into the editor */
  insertVariantText: (text: string) => void
  /** The list of existing variants */
  variants: Variant[]
  /** Function to update the variants list */
  setVariants: (variants: Variant[]) => void
  /** The currently selected variant */
  selectedVariant: SelectedVariant
  /** Function to set the selected variant */
  setSelectedVariant: (variant: Variant | { name: string }) => void
  /** Function to refresh the project data */
  refresh: (id: string) => Promise<void>
  /** Function to save the questionnaire */
  saveQuestionnaire: (updatedVariants?: Variant[]) => Promise<void>
  /** Whether collaborative mode is enabled */
  collaborativeMode?: boolean
  /** Whether the button should be disabled */
  isDisabled?: boolean
  /** When given, the parent asks for the modal (the variant dropdown's "New variant…") and no button is shown */
  open?: boolean
  /** Told when the modal closes, or when opening was refused (not in Master) */
  onOpenChange?: (open: boolean) => void
}

/**
 * CreateVariantModal is a modal component for creating a new variant in a project.
 *
 * @component
 * @param props - The props for the component
 * @returns The rendered CreateVariantModal component
 */
const CreateVariantModal: React.FC<CreateVariantModalProps> = ({
  project,
  insertVariantText,
  variants,
  setVariants,
  selectedVariant,
  setSelectedVariant,
  refresh,
  saveQuestionnaire,
  collaborativeMode = false,
  isDisabled = false,
  open,
  onOpenChange,
}) => {
  const { Title } = Typography
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)

  /**
   * Callback function to handle successful variant creation.
   *
   * @function createVariantCallback
   * @param updatedVariants - The updated variants array
   */
  const createVariantCallback = async (updatedVariants: Variant[]): Promise<void> => {
    notify.success(
      "Variant Created Successfully!",
      "The project has been created successfully."
    )
    await saveQuestionnaire(updatedVariants)
    refresh(project.id)
  }

  /**
   * Shows the modal for creating a new variant.
   * Displays an error notification if a non-master variant is selected.
   *
   * @function showModal
   */
  const showModal = (): void => {
    if (Object.keys(selectedVariant).length === 0) {
      setIsModalVisible(true)
    } else {
      notify.error(
        "Can Only Create Variants in Master!",
        "Please select Master variant to create a new variant."
      )
      onOpenChange?.(false)
    }
  }

  /** Closes the modal and tells a controlling parent. */
  const setVisible = (visible: boolean): void => {
    setIsModalVisible(visible)
    if (!visible) onOpenChange?.(false)
  }

  // A parent asking for the modal goes through the same Master check.
  useEffect(() => {
    if (open) showModal()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <>
      {open === undefined && (
        <DataQButton onClick={showModal} type="secondary" isDisabled={isDisabled}>
          New Variant
          <FontAwesomeIcon icon={faPlusCircle} />
        </DataQButton>
      )}
      <Modal
        open={isModalVisible}
        centered={true}
        footer={false}
        closable={false}
        destroyOnHidden={true}
      >
        <Title style={{ marginBottom: "0.5em" }} level={4}>
          Create New Variant
        </Title>
        <CreateVariantForm
          setVariants={setVariants}
          project={project}
          variants={variants}
          createVariantCallback={createVariantCallback}
          setIsModalVisible={setVisible}
          insertVariantText={insertVariantText}
          setSelectedVariant={setSelectedVariant}
          collaborativeMode={collaborativeMode}
        />
      </Modal>
    </>
  )
}

export default CreateVariantModal
